<template>
  <page-loader>
    <v-container>
      <v-row>
        <v-col>
          <v-card outlined>
            <v-card-title>
              Manual Partners
            </v-card-title>
            <v-card-text>
              <v-data-table
                :headers="headers"
                :items="partnerReports"
                :options="options"
                no-data-text="No report configuration found"
                dense
              >
                <template #item.partner="{ value }">
                  <item-logo :name="value.name" :src="value.logoThumbnail" class="mr-2" />
                  <published-chip class="mr-2" :value="value.published" label no-text small />
                  {{ value.name }}
                </template>
                <template #item.complete="{ value }">
                  <v-icon v-if="value" color="success">check_circle_outline</v-icon>
                  <v-icon v-else color="error">highlight_off</v-icon>
                </template>
                <template v-slot:item.actions="{ item }">
                  <v-icon :disabled="!isPermittedTo('create')" @click="onClickPartner(item)"> backup </v-icon>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
    <v-container>
      <v-expansion-panels>
        <v-expansion-panel>
          <v-expansion-panel-header>
            CSV Upload
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            If you are uploading a CSV, please make sure that you have used the
            <a
              href="https://docs.google.com/spreadsheets/d/1ZHlo_pOE1DA0GQGuOUziVpiBjQDaXaARBxZJIH3nZhA/edit#gid=0"
              target="_blank"
              >CSV template</a
            >
            provided.
            <v-row>
              <v-col class="d-flex align-center">
                <autocomplete-input
                  v-model="entry.partner"
                  :disabled="!isPermittedTo('create')"
                  :items="filteredPartners"
                  class="mr-4"
                  label="Partner"
                />
                <autocomplete-input
                  v-model="entry.currency"
                  :disabled="!isPermittedTo('create')"
                  :items="currencies"
                  class="mr-4"
                  label="Currency"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <drag-and-drop-upload v-model="file" :disabled="isLoading" accept=".csv, application/vnd.ms-excel" />
              </v-col>
            </v-row>
            <v-row>
              <v-col class="d-flex justify-end">
                <v-btn
                  :disabled="!canSaveCsv || isLoading"
                  :loading="isLoading"
                  class="ml-4"
                  color="info"
                  depressed
                  @click="onClickSaveCsv"
                >
                  <v-icon left>save</v-icon>
                  Save events
                </v-btn>
              </v-col>
            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel>
          <v-expansion-panel-header>
            Manual Entry
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-row>
              <v-col class="d-flex align-center">
                <autocomplete-input
                  v-model="entry.partner"
                  :disabled="!isPermittedTo('create')"
                  :items="filteredPartners"
                  class="mr-4"
                  label="Partner"
                />
                <autocomplete-input
                  v-model="entry.currency"
                  :disabled="!isPermittedTo('create')"
                  :items="currencies"
                  class="mr-4"
                  label="Currency"
                />
              </v-col>
            </v-row>
            <v-row
              v-for="(event, index) in entry.events"
              :key="index"
              :class="{
                'grey lighten-4': index % 2 === 1,
              }"
              class="pb-2"
            >
              <v-col class="d-flex align-center">
                <date-picker
                  v-model="event.date"
                  :disabled="!canAddRows"
                  :error="event.date == undefined"
                  format="DDMMYYYY"
                  label="Date"
                  hide-details
                />
              </v-col>
              <v-col>
                <autocomplete-input
                  v-model="event.event_type"
                  :disabled="!canAddRows"
                  :error="event.event_type == undefined"
                  :items="eventTypes"
                  label="Event Type"
                  hide-details
                />
                <autocomplete-input
                  v-model="event.revenue_type"
                  :disabled="!canAddRows"
                  :error="event.revenue_type == undefined"
                  :items="revenueTypes"
                  :item-disabled="isRevenueItemDisabled(event.event_type)"
                  class="mt-4"
                  label="Revenue Type"
                  auto-select-first
                  cache-items
                  hide-details
                />
              </v-col>
              <v-col>
                <autocomplete-input
                  v-model="event.offer_type"
                  :disabled="!canAddRows"
                  :items="offerTypes"
                  label="Offer Type"
                  hide-details
                />
                <autocomplete-input
                  v-model="event.offer"
                  :disabled="!canAddRows || !event.offer_type"
                  :items="filteredOffers.filter(({ type }) => type === event.offer_type)"
                  class="mt-4"
                  label="Offer"
                  hide-details
                />
              </v-col>
              <v-col>
                <autocomplete-input
                  v-model="event.country"
                  :disabled="!canAddRows"
                  :items="countries"
                  label="Country"
                  hide-details
                />
                <autocomplete-input
                  v-model="event.website"
                  :disabled="!canAddRows"
                  :items="sites"
                  class="mt-4"
                  label="Website"
                  hide-details
                />
              </v-col>
              <v-col>
                <text-input v-model="event.click_id" :disabled="!canAddRows" label="Click ID" hide-details />
                <text-input
                  v-model="event.raw_amount"
                  :disabled="!canAddRows"
                  :error="event.raw_amount == undefined"
                  class="mt-4"
                  label="Amount"
                  type="number"
                  hide-details
                />
              </v-col>
              <v-col class="d-flex align-center flex-grow-0 pl-0">
                <v-btn
                  :disabled="!canAddRows || entry.events.length === 1"
                  color="error"
                  depressed
                  small
                  @click="onClickDeleteRow(index)"
                >
                  <v-icon small>close</v-icon>
                </v-btn>
              </v-col>
            </v-row>
            <v-row>
              <v-col class="d-flex justify-end">
                <v-btn :disabled="!canAddRows" class="ml-4" color="primary" depressed @click="onClickAddRow">
                  <v-icon left>add</v-icon>
                  Add new row
                </v-btn>
                <v-btn :disabled="!canAddRows" class="ml-4" color="secondary" depressed @click="onClickCloneRow">
                  <v-icon left>library_add</v-icon>
                  Clone last row
                </v-btn>
              </v-col>
            </v-row>
            <v-row>
              <v-col class="d-flex justify-end">
                <v-btn
                  :disabled="!canSave || isLoading"
                  :loading="isLoading"
                  class="ml-4"
                  color="info"
                  depressed
                  @click="onClickSave"
                >
                  <v-icon left>save</v-icon>
                  Save events
                </v-btn>
              </v-col>
            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-container>
    <v-container>
      <v-row>
        <v-col>
          <v-card outlined>
            <v-card-title>
              Pending Events - £{{ totalValueInTable }}
              <v-spacer></v-spacer>
              <text-input v-model="search" label="Search" append-icon="search" />
            </v-card-title>
            <v-card-text>
              <v-data-table ref="pending" :headers="pendingHeaders" :search="search" :items="eventLog" dense>
                <template #item.date="{ value }">
                  {{ formatDate(value, { separator: '-' }) }}
                </template>
                <template #item.partner="{ value }">
                  <router-link :to="{ name: 'partners.edit', params: { slug: value } }">{{
                    getPartnerNameFromSlug(value)
                  }}</router-link>
                </template>
                <template #item.offer="{ value }">
                  <router-link v-if="value" :to="{ name: 'offers.edit', params: { slug: value } }">{{
                    getOfferNameFromSlug(value)
                  }}</router-link>
                </template>
                <template #item.click_id="{ value }">
                  <valid-click-id :value="value" />
                </template>
                <template #item.amount="{ value }">
                  {{ (value / 100).toFixed(2) }}
                </template>
                <template #item.edit="{ value }">
                  {{ (value / 100).toFixed(2) }}
                </template>
                <template #item.actions="{ item }">
                  <v-icon :disabled="!isPermittedTo('update')" class="mr-2" small @click="onEditItem(item.uuid)">
                    edit
                  </v-icon>
                  <v-icon :disabled="!isPermittedTo('delete')" small @click="onDeleteItem(item.uuid)">
                    delete
                  </v-icon>
                </template>
              </v-data-table>
              <v-row>
                <v-col class="d-flex justify-end">
                  <v-btn
                    :disabled="!isPermittedTo('create') || !eventLog.length"
                    :loading="isLoading"
                    class="ml-4"
                    color="info"
                    depressed
                    @click="onClickUpload"
                  >
                    <v-icon left>cloud_upload</v-icon>
                    Upload data
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
    <exit-modal
      :is-disabled="isLoading"
      :is-loading="isLoading"
      :value="showExitModal"
      @modal-confirm="confirmExitBeforeSave"
      @modal-cancel="cancelExitBeforeSave"
    />
    <edit-event-modal
      :event="editingEvent"
      :is-disabled="isLoading"
      :is-loading="isLoading"
      :value="showEditModal"
      @modal-confirm="onEditConfirm"
      @modal-cancel="onEditCancel"
    >
      <v-row>
        <v-col sm="4">
          <autocomplete-input v-model="editingEvent.partner" :items="filteredPartners" label="Partner" />
        </v-col>
        <v-col sm="4">
          <autocomplete-input v-model="editingEvent.currency" :items="currencies" label="Currency" />
        </v-col>
      </v-row>
      <v-row>
        <v-col sm="4">
          <date-picker
            v-model="editingEvent.date"
            :error="editingEvent.date == undefined"
            format="DDMMYYYY"
            label="Date"
            hide-details
          />
        </v-col>
        <v-col sm="4">
          <autocomplete-input
            v-model="editingEvent.event_type"
            :error="editingEvent.event_type == undefined"
            :items="eventTypes"
            class="mb-1"
            label="Event Type"
            hide-details
          />
        </v-col>
        <v-col sm="4">
          <autocomplete-input
            v-model="editingEvent.revenue_type"
            :error="editingEvent.revenue_type == undefined"
            :items="revenueTypes"
            :item-disabled="isRevenueItemDisabled(editingEvent.event_type)"
            class="mb-1"
            label="Revenue Type"
            auto-select-first
            cache-items
            hide-details
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col sm="4">
          <text-input v-model="editingEvent.click_id" class="mb-1" label="Click ID" hide-details />
        </v-col>
        <v-col sm="4">
          <autocomplete-input v-model="editingEvent.offer_type" :items="offerTypes" label="Offer Type" hide-details />
        </v-col>
        <v-col sm="4">
          <autocomplete-input
            v-model="editingEvent.offer"
            :items="filteredOffers.filter(({ type }) => type === editingEvent.offer_type)"
            class="mb-1"
            label="Offer"
            hide-details
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col sm="4">
          <autocomplete-input
            v-model="editingEvent.country"
            :items="countries"
            class="mb-1"
            label="Country"
            hide-details
          />
        </v-col>
        <v-col sm="4">
          <autocomplete-input v-model="editingEvent.website" :items="sites" class="my-1" label="Website" hide-details />
        </v-col>
        <v-col sm="4">
          <text-input
            v-model="editingEvent.raw_amount"
            :error="editingEvent.raw_amount == undefined"
            class="my-1"
            label="Amount"
            type="number"
            hide-details
          />
        </v-col>
      </v-row>
    </edit-event-modal>
    <generic-modal
      v-if="showUploadModal"
      v-model="showUploadModal"
      :is-disabled="isLoading"
      :is-loading="isLoading"
      confirm-text="Partner complete"
      title="Manual partner report"
      persistent
      @modal-confirm="onModalUploadConfirm"
      @modal-cancel="onModalUploadCancel"
    >
      <v-row>
        <v-col sm="6">
          <table class="report-details">
            <tr>
              <th>URL</th>
              <td><a :href="uploadConfiguration.report.partner_portal.url" target="_blank">Portal</a></td>
            </tr>
            <tr>
              <th>Username</th>
              <td>
                {{ uploadConfiguration.report.partner_portal.username }}
                <copy-to-clipboard
                  label="username"
                  :copy-value="uploadConfiguration.report.partner_portal.username"
                  class="ml-2"
                />
              </td>
            </tr>
            <tr>
              <th>Password</th>
              <td>
                {{ uploadConfiguration.report.partner_portal.password }}
                <copy-to-clipboard
                  label="password"
                  :copy-value="uploadConfiguration.report.partner_portal.password"
                  class="ml-2"
                />
              </td>
            </tr>
          </table>

          <div class="mt-2">
            <span class="font-weight-bold">Instructions</span>
            <ol>
              <li v-for="step in uploadConfiguration.report.partner_portal.steps" :key="step">
                {{ step }}
              </li>
            </ol>
          </div>
        </v-col>
        <v-col sm="5" offset-sm="1">
          <div>
            <item-logo
              :name="uploadConfiguration.partner.name"
              :src="uploadConfiguration.partner.logoThumbnail"
              class="mr-2"
            />{{ uploadConfiguration.partner.name }}
          </div>

          <table class="report-details">
            <tr>
              <th>Portal</th>
              <td>{{ uploadConfiguration.report.partner_portal.name }}</td>
            </tr>
            <tr>
              <th>Type</th>
              <td>{{ uploadConfiguration.report.type }}</td>
            </tr>
          </table>
        </v-col>
      </v-row>
    </generic-modal>
  </page-loader>
</template>

<script>
import { cloneDeep } from 'lodash-es'
import { mapGetters, mapMutations } from 'vuex'

import AutocompleteInput from '@/components/forms/autocomplete-input'
import CopyToClipboard from '@/components/copy-to-clipboard'
import DatePicker from '@/components/forms/date-picker'
import DragAndDropUpload from '@/components/drag-and-drop-upload'
import EditEventModal from '@/views/data/components/edit-event-modal'
import GenericModal from '@/components/modals/generic-modal'
import ItemLogo from '@/components/item-logo'
import PageLoader from '@/components/page-loader'
import PublishedChip from '@/components/chips/published'
import TextInput from '@/components/forms/text-input'
import ValidClickId from '@/components/valid-click-id'

import DataModification from '@/mixins/data-modification'
import PermissionsHandler from '@/mixins/permissions-handler'

import { offers, partners } from '@/services/offers'
import integrations from '@/services/integrations'

import { formatDate } from '@/utils/date'
import storage from '@/utils/storage'

const entryModel = ({
  date = formatDate(new Date(), { format: 'YYYYMMDD', separator: '-' }),
  event_type = null,
  offer_type = null,
  offer = null,
  country = null,
  website = null,
  click_id = null,
  raw_amount = null,
} = {}) => ({
  date,
  event_type,
  offer_type,
  offer,
  country,
  website,
  click_id,
  raw_amount,
})

export default {
  name: 'ManualDataEntry',
  components: {
    AutocompleteInput,
    CopyToClipboard,
    DatePicker,
    DragAndDropUpload,
    EditEventModal,
    GenericModal,
    ItemLogo,
    PageLoader,
    PublishedChip,
    TextInput,
    ValidClickId,
  },
  mixins: [DataModification, PermissionsHandler],
  data: () => ({
    isLoading: false,
    search: '',
    totalValueInTable: 0,
    headers: [
      { text: 'Partner', value: 'partner' },
      { text: 'Portal', value: 'report.partner_portal.name' },
      { text: 'Portal Type', value: 'report.partner_portal.type' },
      { text: 'Report Type', value: 'report.type' },
      { text: 'Uploaded?', align: 'center', value: 'complete', sortable: false },
      { text: 'Actions', align: 'end', value: 'actions', sortable: false },
    ],
    pendingHeaders: [
      { text: 'Partner', value: 'partner' },
      { text: 'Date', value: 'date', width: 110 },
      { text: 'ID', value: 'click_id', sortable: false },
      { text: 'Revenue Type', value: 'revenue_type' },
      { text: 'Offer', value: 'offer' },
      { text: 'Country', value: 'country', sortable: false },
      { text: 'Amount (£)', align: 'end', value: 'amount' },
      { text: 'Actions', align: 'end', value: 'actions', sortable: false },
    ],
    options: {
      itemsPerPage: -1,
    },
    configurations: [],
    eventLog: [],
    completeReports: [],
    offers: [],
    partners: [],
    entry: {
      partner: null,
      currency: 'USD',
      events: [entryModel()],
    },
    file: null,
    eventTypes: [
      { value: 'registration', key: 'Registration' },
      { value: 'qftd', key: 'FTD' },
      { value: 'manualRevenue', key: 'Manual Revenue' },
    ],
    revenueTypes: [
      { value: 'CPA', key: 'CPA', event_types: ['registration', 'qftd', 'manualRevenue'] },
      { value: 'CPL', key: 'CPL', event_types: ['registration', 'manualRevenue'] },
      { value: 'CRG', key: 'CRG', event_types: ['manualRevenue'] },
      { value: 'CPC', key: 'CPC', event_types: ['manualRevenue'] },
      { value: 'News Sales', key: 'News Sales', event_types: ['manualRevenue'] },
      { value: 'News Syndication', key: 'News Syndication', event_types: ['manualRevenue'] },
      { value: 'News Partnerships', key: 'News Partnerships', event_types: ['manualRevenue'] },
      { value: 'Programmatic', key: 'Programmatic', event_types: ['manualRevenue'] },
      { value: 'Subscriptions', key: 'Subscriptions', event_types: ['manualRevenue'] },
      { value: 'FF', key: 'FF', event_types: ['manualRevenue'] },
      { value: 'REVSHARE', key: 'Revenue Share', event_types: ['registration', 'qftd', 'manualRevenue'] },
      { value: 'news', key: 'News', event_types: ['manualRevenue'] },
      { value: 'reviews', key: 'Reviews', event_types: ['manualRevenue'] },
      // { value: 'OTHER', key: 'Other', event_types: ['manualRevenue'] },
    ],
    requiredFields: ['date', 'revenue_type', 'raw_amount'],
    showEditModal: false,
    editingEvent: {},
    uploadConfiguration: null,
    showUploadModal: false,
  }),
  computed: {
    ...mapGetters('general', ['hasPageErrors']),
    ...mapGetters('options', ['formattedOption', 'getItemsForOption']),
    partnerReports() {
      return this.configurations.reduce((reports, config) => {
        const partner = this.partners.find(({ slug }) => slug === config.partner) || { name: 'Unknown Partner' }
        const rows = config.reports.map(report => {
          const isComplete = this.completeReports.find(completedReport => {
            return (
              partner.slug === completedReport.partner &&
              completedReport.type === report.type &&
              completedReport.portal === report.partner_portal.name
            )
          })
          return {
            complete: !!isComplete,
            partner,
            report,
          }
        })
        return reports.concat(rows)
      }, [])
    },
    canSave() {
      return (
        this.canAddRows &&
        this.entry.events.reduce((canSave, event) => {
          const requiredDefined = Object.entries(event).every(
            ([key, value]) => !this.requiredFields.includes(key) || value != undefined
          )
          if (!requiredDefined) canSave = false
          return canSave
        }, true)
      )
    },
    canSaveCsv() {
      return this.canAddRows && this.entry.partner && this.entry.currency && this.file
    },
    countries() {
      return this.getItemsForOption('countries')
    },
    currencies() {
      return this.getItemsForOption('mainCurrencies')
    },
    sites() {
      return this.getItemsForOption('sites')
    },
    offerTypes() {
      return this.getItemsForOption('offerTypes')
    },
    filteredOffers() {
      return this.offers.map(({ name, slug, type }) => ({
        key: name || slug,
        type,
        value: slug,
      }))
    },
    filteredPartners() {
      return this.partners.map(({ name, slug }) => ({
        key: name || slug,
        value: slug,
      }))
    },
    canAddRows() {
      return !!this.entry.partner && this.isPermittedTo('create')
    },
  },
  async created() {
    this.setBeforeUnloadListener(this.onBeforeUnload)
    this.getCompleteReports()
    await this.getOffersAndPartners()
    await this.getConfigurations()
    await this.getEventLog()
    this.totalValueInTable = this.updateSumTotal()
    this.setPageLoading(false)
  },
  beforeDestroy() {
    this.removeBeforeUnloadListener(this.onBeforeUnload)
  },
  beforeRouteLeave(to, from, next) {
    if (!this.entry.partner) return next()
    this.handleExitBeforeSave(next)
  },
  methods: {
    ...mapMutations('general', ['setPageLoading', 'setPageErrors']),
    formatDate(str, opts) {
      return formatDate(str, opts)
    },
    updateSumTotal() {
      const filteredItems =
        this.$refs &&
        this.$refs.pending &&
        this.$refs.pending.$children &&
        this.$refs.pending.$children[0].filteredItems

      const toSum = filteredItems || this.eventLog
      const sum = toSum.reduce((total, { amount }) => total + amount, 0)

      this.totalValueInTable = sum ? (sum / 100).toFixed(2) : 0
    },
    filteredRevenueTypes(event_type) {
      return this.revenueTypes.filter(({ event_types }) => event_types.includes(event_type))
    },
    isRevenueItemDisabled(event_type) {
      return ({ event_types }) => !event_types.includes(event_type)
    },
    getPartnerNameFromSlug(slug) {
      const found = this.filteredPartners.find(partner => partner.value === slug)
      return found ? found.key : slug
    },
    getOfferNameFromSlug(slug) {
      const found = this.filteredOffers.find(offer => offer.value === slug)
      return found ? found.key : slug
    },
    async getConfigurations() {
      this.isLoading = true

      try {
        const { partners } = await integrations.getConfig('manual')
        this.configurations = partners
      } catch (error) {
        this.setPageErrors(true)
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve partner configurations',
        })
      }

      this.isLoading = false
    },
    async getEventLog() {
      this.isLoading = true

      try {
        const response = await integrations.getEventLog({
          revenue: true,
          pending: true,
          source: 'manual',
        })
        this.$set(this, 'eventLog', response.events)
      } catch (error) {
        this.setPageErrors(true)
        this.$notify({
          type: 'error',
          text: 'Unable to fetch event logs',
        })
      }

      this.isLoading = false
    },
    onEventTypeChange(index) {
      this.entry.events[index].revenue_type = null
    },
    onEditItem(uuid) {
      this.editingEvent = this.eventLog.find(event => event.uuid === uuid)
      this.showEditModal = true
    },
    async onDeleteItem(uuid) {
      this.isLoading = true
      await integrations.deleteEvent(uuid)
      await this.getEventLog()
      this.isLoading = false
    },
    async onClickUpload() {
      this.isLoading = true

      try {
        await integrations.sendToEventCollector({ revenue: true, source: 'manual' })
        await this.getEventLog()
        this.$notify({
          type: 'success',
          text: 'Events uploaded',
        })
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to upload events',
        })
      }

      this.isLoading = false
    },
    onClickAddRow() {
      this.entry.events.push(entryModel())
    },
    onClickCloneRow() {
      const lastItem = cloneDeep(this.entry.events[this.entry.events.length - 1])
      this.entry.events.push(lastItem)
    },
    onClickDeleteRow(index) {
      const updatedEntries = this.entry.events.slice(0, index).concat(this.entry.events.slice(index + 1))
      this.$set(this.entry, 'events', updatedEntries)
    },
    async onClickSave() {
      this.isLoading = false

      try {
        await integrations.saveManualEvents(this.entry)
        await this.getEventLog()
        this.entry = {
          partner: null,
          currency: 'USD',
          events: [entryModel()],
        }
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to save events',
        })
      }

      this.isLoading = false
    },
    async onClickSaveCsv() {
      this.isLoading = false

      try {
        await integrations.saveManualCsvEvents({
          partner: this.entry.partner,
          currency: this.entry.currency,
          events: this.file,
        })
        await this.getEventLog()
        this.entry = {
          partner: null,
          currency: 'USD',
          events: [entryModel()],
        }
        this.file = null
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to save events',
        })
      }

      this.isLoading = false
    },
    onBeforeUnload(event) {
      if (!this.entry.partner) return next()
      this.confirmUnload(event)
    },
    async getOffersAndPartners() {
      this.isLoading = true

      try {
        const { data: offerData } = await offers.getAllOffers({ compact: true })
        this.offers = offerData
        const { data: partnerData } = await partners.getAllPartners({ compact: true })
        this.partners = partnerData
      } catch (error) {
        this.setPageErrors(true)
        this.$notify({
          type: 'error',
          text: 'Unable to retrieve Offers & Partners',
        })
      }

      this.isLoading = false
    },
    async onEditConfirm() {
      this.isLoading = true

      try {
        await integrations.editEvent(this.editingEvent.uuid, this.editingEvent)
        await this.getEventLog()
        this.showEditModal = false
        this.editingEvent = {}
        this.$notify({
          type: 'success',
          text: 'Event Updated',
        })
      } catch (error) {
        this.$notify({
          type: 'error',
          text: 'Unable to save event',
        })
      }

      this.isLoading = false
    },
    onEditCancel() {
      this.showEditModal = false
      this.editingEvent = {}
    },
    getCompleteReports() {
      const { reports = [] } = storage.get('manual-report-uploads') || {}
      this.completeReports = reports
    },
    setCompleteReport(partner, report) {
      const { reports = [] } = storage.get('manual-report-uploads') || {}

      storage.set(
        'manual-report-uploads',
        {
          reports: [
            ...reports,
            {
              partner: partner.slug,
              portal: report.partner_portal.name,
              type: report.type,
            },
          ],
        },
        new Date(new Date().setHours(23, 59, 59))
      )
    },
    onClickPartner(reportData) {
      this.uploadConfiguration = reportData
      this.showUploadModal = true
    },
    async onModalUploadConfirm() {
      this.isLoading = true

      const { partner, report } = this.uploadConfiguration

      this.setCompleteReport(partner, report)
      this.getCompleteReports()

      this.uploadConfiguration = null
      this.showUploadModal = false

      this.isLoading = false
    },
    onModalUploadCancel() {
      this.uploadConfiguration = null
      this.showUploadModal = false
    },
  },
}
</script>

<style lang="scss" scoped>
.report-details {
  width: 100%;

  th {
    text-align: left;
  }
}
</style>
